import { Link, useNavigate } from 'react-router-dom'
import { where } from 'firebase/firestore'
import { useAuth } from '../../lib/auth'
import { useEntitlements, useTourAllowance } from '../../lib/entitlements'
import { useTours } from '../../lib/tours'
import { useCollection } from '../../lib/firestore'
import { formatShortDate } from '../../lib/format'
import type { Show, Tour, TourCrew, WithId } from '../../types/models'
import MobileHeader, { MobileScreen, SectionLabel } from '../../components/mobile/MobileHeader'
import Icon from '../../components/Icon'
import Poster from '../../components/Poster'
import Empty from '../../components/Empty'

/**
 * Tours.
 *
 * The current tour gets a full-width card with the detail worth knowing before
 * you tap in; everything else is artwork in a horizontal rail, because a tour
 * is recognised by its poster long before its name is read.
 */
export default function MobileTours() {
  const { uid } = useAuth()
  const navigate = useNavigate()
  const { tours, loading } = useTours(uid ?? undefined)
  const allowance = useTourAllowance(tours, uid ?? undefined)
  const { permits } = useEntitlements()

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const current = tours.filter(
    (tour) => tour.startDate.toDate() <= today && tour.endDate.toDate() >= today,
  )
  const upcoming = tours.filter((tour) => tour.startDate.toDate() > today)
  const past = tours
    .filter((tour) => tour.endDate.toDate() < today)
    .sort((a, b) => b.endDate.toMillis() - a.endDate.toMillis())

  return (
    <>
      <MobileHeader
        title="Tours"
        back={false}
        action={
          allowance.allowed
            ? {
                icon: 'plus',
                label: 'New tour',
                accent: true,
                onClick: () => navigate('/tours/new'),
              }
            : undefined
        }
      />
      <MobileScreen>
        {/* The phone tab bar is capped at five and already full, so the
            roster gets its entry point here rather than a sixth tab. */}
        {permits('artist_profiles') && (
          <Link
            to="/artists"
            className="panel row"
            style={{ gap: 10, padding: '13px 16px', marginBottom: 4 }}
          >
            <Icon name="mic" size={17} style={{ color: 'var(--accent-text)' }} />
            <span className="grow" style={{ fontWeight: 600 }}>
              Artists
            </span>
            <Icon name="chevronRight" size={15} style={{ color: 'var(--text-tertiary)' }} />
          </Link>
        )}

        {!allowance.allowed && (
          <p className="panel panel-pad t-caption" style={{ borderColor: 'var(--accent)' }}>
            {allowance.message} Everything you've already made stays readable and exportable.
          </p>
        )}

        {!loading && tours.length === 0 && (
          <Empty
            title="No tours yet"
            message="Every show, flight and call sheet hangs off a tour."
            action={
              allowance.allowed
                ? { label: 'Create your first tour', onClick: () => navigate('/tours/new') }
                : undefined
            }
          />
        )}

        {current.map((tour) => (
          <section key={tour.id} className="stack" style={{ gap: 8 }}>
            <SectionLabel>Current tour</SectionLabel>
            <CurrentTourCard tour={tour} />
          </section>
        ))}

        {upcoming.length > 0 && (
          <section className="stack" style={{ gap: 8 }}>
            <SectionLabel>Upcoming</SectionLabel>
            <PosterRail tours={upcoming} />
          </section>
        )}

        {past.length > 0 && (
          <section className="stack" style={{ gap: 8 }}>
            <SectionLabel>Past tours</SectionLabel>
            <PosterRail tours={past} muted />
          </section>
        )}
      </MobileScreen>
    </>
  )
}

function CurrentTourCard({ tour }: { tour: WithId<Tour> }) {
  const shows = useCollection<Show>('shows', [where('tourId', '==', tour.id)])
  const crew = useCollection<TourCrew>('tourCrew', [where('tourId', '==', tour.id)])
  const manager = crew.data.find((member) => member.roles?.includes('Tour Manager'))

  return (
    <Link
      to={`/tours/${tour.id}`}
      className="panel panel-signalled"
      style={{ display: 'block', padding: 16 }}
    >
      <div className="row" style={{ gap: 14, alignItems: 'flex-start' }}>
        <Poster url={tour.posterURL} width={86} />

        <div className="stack grow" style={{ gap: 8, minWidth: 0 }}>
          <span className="t-display-m" style={{ lineHeight: 1.05 }}>
            {tour.tourName}
          </span>

          <div className="stack" style={{ gap: 6 }}>
            <DetailRow icon="calendar">
              {formatShortDate(tour.startDate)} – {formatShortDate(tour.endDate)}
            </DetailRow>
            <DetailRow icon="account">{manager?.name ?? 'No tour manager'}</DetailRow>
            <DetailRow icon="mic">
              {shows.data.length} show{shows.data.length === 1 ? '' : 's'}
            </DetailRow>
            <DetailRow icon="people">
              {crew.data.length} crew member{crew.data.length === 1 ? '' : 's'}
            </DetailRow>
          </div>
        </div>
      </div>
    </Link>
  )
}

function DetailRow({
  icon,
  children,
}: {
  icon: 'calendar' | 'account' | 'mic' | 'people'
  children: React.ReactNode
}) {
  return (
    <span className="row t-caption" style={{ gap: 9, minWidth: 0 }}>
      <Icon name={icon} size={15} style={{ color: 'var(--text-tertiary)' }} />
      <span className="truncate">{children}</span>
    </span>
  )
}

function PosterRail({ tours, muted }: { tours: WithId<Tour>[]; muted?: boolean }) {
  return (
    <div
      className="scroll-x"
      style={{
        display: 'flex',
        gap: 14,
        overflowX: 'auto',
        // Bleeds to the screen edge so the rail reads as scrollable rather
        // than as a row that happens to be cut off.
        margin: '0 calc(var(--gutter) * -1)',
        padding: '0 var(--gutter) 4px',
      }}
    >
      {tours.map((tour) => (
        <Link
          key={tour.id}
          to={`/tours/${tour.id}`}
          className="stack"
          style={{ gap: 8, width: 156, flex: 'none', opacity: muted ? 0.7 : 1 }}
        >
          <Poster url={tour.posterURL} width={156} />
          <span className="stack" style={{ gap: 1, minWidth: 0 }}>
            <span className="truncate" style={{ fontWeight: 600 }}>
              {tour.artist}
            </span>
            <span className="t-caption truncate">{tour.tourName}</span>
          </span>
        </Link>
      ))}
    </div>
  )
}
